import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Box } from '@material-ui/core';

import { Button } from 'common/components/buttons/Button';

import { IAvailableStore } from '../../types';
import { StoreCard } from '../StoreCard';
import { ToggleViewBar } from '../ToggleViewBar';
import { useStateContext } from '../../context';
import { calcDistanceKm } from '../../utils';

interface IProps {
    availableStores: IAvailableStore[];
    className?: string;
    onClickStore(store: IAvailableStore): void;
}

const BaseStoreList: React.FC<IProps> = ({
    availableStores,
    className,
    onClickStore,
}) => {
    const { centerLocation, searchLocation, selectedStore } = useStateContext();
    const listRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const [showAllStores, toggleShowAllStores] = useState(false);

    useEffect(() => {
        if (!listRef.current) return;

        listRef.current.scrollTop = 0;
    }, [searchLocation]);

    const referenceLocation = searchLocation || centerLocation;

    if (!referenceLocation) return null;

    const storesSortedByDistance = availableStores
        .map((store) => ({
            ...store,
            distanceKm: calcDistanceKm(
                {
                    lat: store.lat,
                    lng: store.lng,
                },
                referenceLocation
            ),
        }))
        .sort((storeA, storeB) => {
            // Selected store should always be the first element
            if (selectedStore) {
                if (selectedStore.id === storeA.id) return -1;
                if (selectedStore.id === storeB.id) return 1;
            }

            return storeA.distanceKm - storeB.distanceKm;
        });

    const displayedStores = showAllStores
        ? storesSortedByDistance
        : storesSortedByDistance.slice(0, 20);

    const storeCardList = displayedStores.map((store) => {
        const isSelectedStore = selectedStore && selectedStore.id === store.id;

        return (
            <StoreCard
                hideDistance
                key={store.id}
                selected={isSelectedStore}
                store={store}
                onClick={() => onClickStore(store)}
            />
        );
    });

    return (
        <div className={className} ref={listRef}>
            <ToggleViewBar />
            {storeCardList}

            <Box mt={4}>
                <Button
                    fullWidth
                    onClick={() => toggleShowAllStores(!showAllStores)}
                >
                    {showAllStores
                        ? t('appointment.showLessStores')
                        : t('appointment.showAllStores')}
                </Button>
            </Box>
        </div>
    );
};

export const StoreList = styled(BaseStoreList)`
    flex: 1;
    padding: 30px 0;

    > p {
        margin-top: 0;
        line-height: 26px;
    }
`;
